import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { TransferAggregationCrumbs } from 'subscription/constants/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { toast } from 'react-toastify';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import validationSchemaTransferAggregation from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionButtons from 'subscription/components/shared/buttons';
import SubscriptionTransferOtherMunicipalityAct from 'subscription/components/transfer-other-municipality';
import { transformerSubscriptionTransferOtherMunicipalityData } from 'subscription/utils/transfer-other-municipality.response-transformer';
import { SubscriptionTransferOtherMunicipalityFormVM } from 'subscription/view-models/subscription-transfer-other-municipality';
import { transformerRequestSubscriptionTransferAireData } from 'subscription/utils/transfer-other-municipality.request-transformer';

const TransferAggregation = (): React.ReactElement => {
  const navigate = useNavigate();

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    SubscriptionTransferOtherMunicipalityFormVM
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {
        criteriRicerca: {
          codiceFiscale: fiscalCode || undefined,
          idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          soggettoAIRE: 'S',
        },
        datiRichiesta: {
          ricercaNazionale: nationalSearch ? '1' : '0',
          comuneResidenza:
            filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
            filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
            filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
            filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
              ? {
                  siglaProvinciaIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.province ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.province ||
                    undefined,
                  codiceIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                    undefined,
                  nomeComune:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                      ?.description ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                      ?.description ||
                    undefined,
                }
              : undefined,
          dataRiferimentoRichiesta:
            filterTable.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
            undefined,
        },
      },
    },
    transformer: (response) => transformerSubscriptionTransferOtherMunicipalityData(response),
  });

  const [responseRegister, register] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-aire-anpr/trasferimento-altro-comune`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegister?.data as SaveDataAnpr;

  const handleSubmit = useEventCallback((values: SubscriptionTransferOtherMunicipalityFormVM) => {
    const data = transformerRequestSubscriptionTransferAireData(values);
    (
      register({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;
      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate('/iscrizioni');
    });
  });

  const handleExit = useCallback(() => {
    navigate('/iscrizioni');
  }, [navigate]);

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const data = (anpr?.data || {}) as SubscriptionTransferOtherMunicipalityFormVM;

  return (
    <Fragment>
      <Breadcrumbs items={TransferAggregationCrumbs} />
      <div>
        <PmiTitle title="Iscrizione AIRE per trasferimento - Aggregazione a famiglia" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {anpr.data && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={
            {
              ...anpr.data,
              editDataControl: {
                ...data?.editDataControl,
                subscribeTo: '5', // "Altra famiglia" fisso
              },
              generality: {
                ...data?.generality,
                aire: 'S',
                isForeign: true,
              },
              birthdayDocument: {
                ...data?.birthdayDocument,
                paper: data?.birthdayDocument?.paper
                  ? {
                      ...data?.birthdayDocument?.paper,
                      transcribed: true,
                    }
                  : undefined,
              },
            } as SubscriptionTransferOtherMunicipalityFormVM
          }
          onSubmit={handleSubmit}
          validationSchema={validationSchemaTransferAggregation}
        >
          <Form>
            <Grid container spacing={2}>
              <SubscriptionButtons handleExit={handleExit} />
              <Grid item xs={12} md={10}>
                <SubscriptionTransferOtherMunicipalityAct
                  isAire
                  generalityWithResearch
                  isTransferAggregation
                  hasResearchSipo
                  urlEditSipo="/iscrizioni/aire-trasferimento-aggregazione/persona-sipo"
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

TransferAggregation.displayName = 'Transfer New page component';

export default TransferAggregation;
